import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { clearExploreActionState, type SidebarView, selectAutoFocusSearch, selectExploreActionMenuPinId, selectSidebarView, setActiveChecklistSubcategory, setActiveCustomChecklistId, setActiveHeroActionId, setAskAnythingResult, setAutoFocusSearch, setHeroActionTriggerPinId, setSharedChecklistPayload, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { clearSelectedHeatmapEvent, selectCurrentMapInfo, selectExternalMapId, selectIsEditingCustomPin, selectMapData, selectSelectedHeatmapEvent, selectSelectedMarkerId, selectTitleName } from '@repo/store/maps/slices/mapData';
import { HostMode, HostType } from '@repo/types/host';
import { MobileDrawer } from '@repo/ui/components/mobile-drawer/mobile-drawer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChecklistSidebarView } from '../checklist/checklist-sidebar-view';
import { CustomChecklistSidebarView } from '../checklist/custom-checklist-sidebar-view';
import { SharedChecklistSidebarView } from '../checklist/shared-checklist-sidebar-view';
import { useChecklistNavigation } from '../checklist/use-checklist-navigation';
import { TeleportCtaCard } from '../components/map-feature-cta-card';
import { AskAnythingSidebarView } from '../explore/ask-anything-sidebar-view';
import { resolveExploreActions } from '../explore/explore-actions';
import { ExploreSidebarView } from '../explore/explore-sidebar-view';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { useSessionHistory } from '../game-map/hooks/useSessionHistory';
import { useSharedChecklistUrlInitializer } from '../game-map/hooks/useSharedChecklistUrlInitializer';
import { HeatmapEventInfo } from '../location-info/heatmap-event-info';
import { HeroActionSidebarView } from '../location-info/hero-action-sidebar-view';
import { useHeroActionForPin } from '../location-info/hooks/useHeroActionForPin';
import { useLocationInfo } from '../location-info/hooks/useLocationInfo';
import { useStaticNearbyPins } from '../location-info/hooks/useStaticNearbyPins';
import { SessionHistoryView } from '../map-sidebar/session-history-view';
import { MapLocationContent } from './map-location-content';
import { MobileSidebarContent } from './mobile-sidebar-content';

/** Minimum horizontal swipe distance (px) to trigger a view dismissal. */
const SWIPE_DISMISS_THRESHOLD = 80;

/** Drawer height for icon row, title, padding, and action buttons. */
const PIN_DRAWER_BASE_HEIGHT = 160;
/** Additional height for hero media (net of icon/title overlap). */
const PIN_DRAWER_MEDIA_HEIGHT = 100;
/** Additional height for the description area. */
const PIN_DRAWER_DESCRIPTION_HEIGHT = 120;
/** Additional height for the nearby pins navigation bar. */
const PIN_DRAWER_NEARBY_PINS_HEIGHT = 56;
/** Additional height for the hero action row stacked above the nearby pins bar. */
const PIN_DRAWER_HERO_ACTION_HEIGHT = 64;
const PIN_DRAWER_EXPLORE_ACTION_MENU_BASE_HEIGHT = 320;
const PIN_DRAWER_EXPLORE_ACTION_MENU_ROW_HEIGHT = 48;

/** Snap rest cap — keeps the drawer's top edge well below the toolbar's filter chips. */
const DRAWER_TOP_RESERVE = 120;
/** Toolbar top offset (16) + chip row height (48) + 8px gap. Add safe-area-inset-top for the full drag cap. */
const DRAWER_DRAG_RESERVE_BASE = 72;
/** Peek height when the drawer is closed on the default (no-pin) view. */
export const DEFAULT_VIEW_PEEK_HEIGHT = 100;
/** Peek height for all other views (pin info, checklist, explore) — tight, since those open immediately. */
const PIN_VIEW_PEEK_HEIGHT = 100;
/** Gap between the floating map-controls column and the top of the closed drawer peek. */
export const MAP_CONTROLS_DRAWER_GAP = 12;
type ViewKey = SidebarView | 'location';
interface MapMobilePaneProps {
  teleportCta: {
    close: () => void;
    handleDownloadClick: () => void;
    isOpen: boolean;
  };
}
const getViewKey = (selectedMarkerId: string | null, selectedHeatmapEventId: string | null, mobileView: SidebarView): ViewKey => {
  if (selectedMarkerId || selectedHeatmapEventId) return 'location';
  return mobileView;
};
export const MapMobilePane = t0 => {
  const $ = _c(134);
  const {
    teleportCta
  } = t0;
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const mobileView = useAppSelector(selectSidebarView);
  const exploreActionMenuPinId = useAppSelector(selectExploreActionMenuPinId);
  const hostMode = useHostMode();
  const hostType = useHostType();
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const selectedHeatmapEvent = useAppSelector(selectSelectedHeatmapEvent);
  const autoFocusSearch = useAppSelector(selectAutoFocusSearch);
  const mapData = useAppSelector(selectMapData);
  const {
    selectedLocation
  } = useLocationInfo();
  const nearbyPins = useStaticNearbyPins(selectedLocation, mapData);
  const {
    clearSelectedMarkerInMap,
    selectMarkerInMap
  } = useMapPinOperations();
  const {
    hideSessionTrail,
    setTimelineVisible
  } = useSessionHistory();
  const {
    enterChecklist: enterChecklistFilters,
    exitChecklist: exitChecklistFilters
  } = useChecklistNavigation();
  const analytics = useAnalytics();
  const externalMapId = useAppSelector(selectExternalMapId);
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const titleName = useAppSelector(selectTitleName);
  const isEditingCustomPin = useAppSelector(selectIsEditingCustomPin);
  const selectedHeatmapEventId = selectedHeatmapEvent?.id ?? null;
  let t1;
  if ($[0] !== mobileView || $[1] !== selectedHeatmapEventId || $[2] !== selectedMarkerId) {
    t1 = getViewKey(selectedMarkerId, selectedHeatmapEventId, mobileView);
    $[0] = mobileView;
    $[1] = selectedHeatmapEventId;
    $[2] = selectedMarkerId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const currentViewKey = t1;
  const isSelectedPinExploreActionMenu = !!selectedMarkerId && exploreActionMenuPinId === selectedMarkerId;
  const [safeAreaTop, setSafeAreaTop] = useState(0);
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const measure = () => {
        const probe = document.createElement("div");
        probe.style.cssText = "position:fixed;top:0;left:0;height:env(safe-area-inset-top,0);visibility:hidden;pointer-events:none;";
        document.body.appendChild(probe);
        const h = probe.getBoundingClientRect().height;
        document.body.removeChild(probe);
        setSafeAreaTop(h);
      };
      measure();
      window.addEventListener("resize", measure);
      window.addEventListener("orientationchange", measure);
      return () => {
        window.removeEventListener("resize", measure);
        window.removeEventListener("orientationchange", measure);
      };
    };
    t3 = [];
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const drawerDragTopReserve = DRAWER_DRAG_RESERVE_BASE + safeAreaTop;
  const hasMedia = !!(selectedLocation?.properties?.imageId || selectedLocation?.properties?.imagePath || selectedLocation?.properties?.videoPath);
  const hasDescription = !!selectedLocation?.properties?.description;
  const hasNearbyPins = nearbyPins.length > 1;
  const hasHeroAction = !!useHeroActionForPin(selectedLocation?.properties.externalId);
  let t4;
  bb0: {
    if (!isSelectedPinExploreActionMenu) {
      t4 = 0;
      break bb0;
    }
    const t5 = selectedMarkerId ?? undefined;
    let t6;
    if ($[6] !== externalMapId || $[7] !== mapData || $[8] !== t5) {
      t6 = resolveExploreActions({
        mapData,
        mapExternalId: externalMapId,
        pinId: t5
      });
      $[6] = externalMapId;
      $[7] = mapData;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t4 = t6.length;
  }
  const exploreActionCount = t4;
  const exploreActionMenuHeight = PIN_DRAWER_EXPLORE_ACTION_MENU_BASE_HEIGHT + Math.max(exploreActionCount, 1) * PIN_DRAWER_EXPLORE_ACTION_MENU_ROW_HEIGHT;
  let t5;
  if ($[10] !== exploreActionMenuHeight || $[11] !== hasDescription || $[12] !== hasHeroAction || $[13] !== hasMedia || $[14] !== hasNearbyPins || $[15] !== isEditingCustomPin || $[16] !== isSelectedPinExploreActionMenu || $[17] !== selectedMarkerId || $[18] !== teleportCta) {
    const getLocationContentHeight = () => {
      if (!selectedMarkerId || isEditingCustomPin) {
        return;
      }
      if (teleportCta.isOpen) {
        return 332;
      }
      if (isSelectedPinExploreActionMenu) {
        return exploreActionMenuHeight;
      }
      let h_0 = PIN_DRAWER_BASE_HEIGHT;
      if (hasMedia) {
        h_0 = PIN_DRAWER_BASE_HEIGHT + PIN_DRAWER_MEDIA_HEIGHT;
      }
      if (hasDescription) {
        h_0 = h_0 + PIN_DRAWER_DESCRIPTION_HEIGHT;
      }
      if (hasHeroAction) {
        h_0 = h_0 + PIN_DRAWER_HERO_ACTION_HEIGHT;
      }
      if (hasNearbyPins) {
        h_0 = h_0 + PIN_DRAWER_NEARBY_PINS_HEIGHT;
      }
      return h_0;
    };
    t5 = getLocationContentHeight();
    $[10] = exploreActionMenuHeight;
    $[11] = hasDescription;
    $[12] = hasHeroAction;
    $[13] = hasMedia;
    $[14] = hasNearbyPins;
    $[15] = isEditingCustomPin;
    $[16] = isSelectedPinExploreActionMenu;
    $[17] = selectedMarkerId;
    $[18] = teleportCta;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const locationContentHeight = t5;
  let t6;
  bb1: {
    if (!locationContentHeight) {
      t6 = undefined;
      break bb1;
    }
    const vh = window.innerHeight;
    const range = vh - 100;
    const contentFraction = Math.max(0, Math.min(0.9, (locationContentHeight - 100) / range));
    let t7;
    if ($[20] !== contentFraction) {
      t7 = [0, contentFraction, 0.9];
      $[20] = contentFraction;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t6 = t7;
  }
  const locationSnapPoints = t6;
  let t7;
  let t8;
  if ($[22] !== autoFocusSearch || $[23] !== currentViewKey || $[24] !== dispatch) {
    t7 = () => {
      if (autoFocusSearch && currentViewKey !== "default") {
        dispatch(setAutoFocusSearch(false));
      }
    };
    t8 = [autoFocusSearch, currentViewKey, dispatch];
    $[22] = autoFocusSearch;
    $[23] = currentViewKey;
    $[24] = dispatch;
    $[25] = t7;
    $[26] = t8;
  } else {
    t7 = $[25];
    t8 = $[26];
  }
  useEffect(t7, t8);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = css({
      animation: "[slide-fade-in-right 0.2s ease-out]"
    });
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const viewEnterStyles = t9;
  let t10;
  if ($[28] !== dispatch || $[29] !== enterChecklistFilters) {
    t10 = () => {
      enterChecklistFilters();
      dispatch(setActiveChecklistSubcategory(null));
      dispatch(setSidebarView("checklist"));
      setIsOpen(true);
    };
    $[28] = dispatch;
    $[29] = enterChecklistFilters;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const enterChecklist = t10;
  let t11;
  if ($[31] !== dispatch || $[32] !== exitChecklistFilters) {
    t11 = () => {
      exitChecklistFilters();
      dispatch(setActiveChecklistSubcategory(null));
      dispatch(setSidebarView("default"));
    };
    $[31] = dispatch;
    $[32] = exitChecklistFilters;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const exitChecklist = t11;
  let t12;
  if ($[34] !== dispatch) {
    t12 = () => {
      dispatch(setSidebarView("default"));
      dispatch(clearExploreActionState());
    };
    $[34] = dispatch;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const exitExplore = t12;
  let t13;
  if ($[36] !== dispatch) {
    t13 = () => {
      dispatch(setSidebarView("checklist"));
      dispatch(setActiveCustomChecklistId(null));
    };
    $[36] = dispatch;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const exitCustomChecklist = t13;
  let t14;
  if ($[38] !== dispatch) {
    t14 = () => {
      dispatch(setSidebarView("default"));
      dispatch(setSharedChecklistPayload(null));
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (url.searchParams.has("cl")) {
          url.searchParams.delete("cl");
          window.history.replaceState(null, "", url.toString());
        }
      }
    };
    $[38] = dispatch;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const exitSharedChecklist = t14;
  useSharedChecklistUrlInitializer();
  let t15;
  if ($[40] !== dispatch) {
    t15 = () => {
      dispatch(setSidebarView("default"));
      dispatch(setActiveHeroActionId(null));
      dispatch(setHeroActionTriggerPinId(null));
    };
    $[40] = dispatch;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const exitHeroAction = t15;
  let t16;
  if ($[42] !== dispatch) {
    t16 = () => {
      dispatch(setSidebarView("default"));
      dispatch(setAskAnythingResult(null));
    };
    $[42] = dispatch;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  const exitAskAnything = t16;
  let t17;
  if ($[44] !== analytics || $[45] !== currentMapInfo?.name || $[46] !== dispatch || $[47] !== externalMapId || $[48] !== hostType || $[49] !== titleName) {
    t17 = () => {
      dispatch(setSidebarView("sessionHistory"));
      analytics.event("game_map_timeline_open", {
        mapId: externalMapId,
        mapName: currentMapInfo?.name,
        titleName,
        platform: hostType
      });
    };
    $[44] = analytics;
    $[45] = currentMapInfo?.name;
    $[46] = dispatch;
    $[47] = externalMapId;
    $[48] = hostType;
    $[49] = titleName;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  currentMapInfo?.name;
  const enterSessionHistory = t17;
  let t18;
  if ($[51] !== dispatch || $[52] !== hideSessionTrail || $[53] !== setTimelineVisible) {
    t18 = () => {
      dispatch(setSidebarView("default"));
      hideSessionTrail();
      setTimelineVisible(false);
    };
    $[51] = dispatch;
    $[52] = hideSessionTrail;
    $[53] = setTimelineVisible;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  const exitSessionHistory = t18;
  let t19;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e => {
      if (e.touches[0]) {
        touchStartXRef.current = e.touches[0].clientX;
        touchStartYRef.current = e.touches[0].clientY;
      }
    };
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const handleContentTouchStart = t19;
  let t20;
  if ($[56] !== exitAskAnything || $[57] !== exitChecklist || $[58] !== exitExplore || $[59] !== exitHeroAction || $[60] !== exitSessionHistory || $[61] !== mobileView) {
    t20 = e_0 => {
      if (!e_0.changedTouches[0]) {
        return;
      }
      const deltaX = e_0.changedTouches[0].clientX - touchStartXRef.current;
      const deltaY = Math.abs(e_0.changedTouches[0].clientY - touchStartYRef.current);
      const isHorizontalSwipe = deltaX > SWIPE_DISMISS_THRESHOLD && deltaX > deltaY;
      if (!isHorizontalSwipe) {
        return;
      }
      if (mobileView === "sessionHistory") {
        exitSessionHistory();
      } else {
        if (mobileView === "checklist") {
          exitChecklist();
        } else {
          if (mobileView === "explore") {
            exitExplore();
          } else {
            if (mobileView === "askAnything") {
              exitAskAnything();
            } else {
              if (mobileView === "heroAction") {
                exitHeroAction();
              }
            }
          }
        }
      }
    };
    $[56] = exitAskAnything;
    $[57] = exitChecklist;
    $[58] = exitExplore;
    $[59] = exitHeroAction;
    $[60] = exitSessionHistory;
    $[61] = mobileView;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  const handleSwipeDismiss = t20;
  const prevSelectedMarkerRef = useRef(selectedMarkerId);
  const prevSelectedHeatmapRef = useRef(selectedHeatmapEventId);
  let t21;
  let t22;
  if ($[63] !== isOpen || $[64] !== isSearchFocused || $[65] !== mobileView || $[66] !== selectedHeatmapEventId || $[67] !== selectedMarkerId) {
    t21 = () => {
      if ((selectedMarkerId || selectedHeatmapEventId) && !isOpen) {
        setIsOpen(true);
      }
      if (!selectedMarkerId && !selectedHeatmapEventId && (prevSelectedMarkerRef.current || prevSelectedHeatmapRef.current) && isOpen && !isSearchFocused && mobileView === "default") {
        setIsOpen(false);
      }
      prevSelectedMarkerRef.current = selectedMarkerId;
      prevSelectedHeatmapRef.current = selectedHeatmapEventId;
    };
    t22 = [selectedMarkerId, selectedHeatmapEventId, isOpen, isSearchFocused, mobileView];
    $[63] = isOpen;
    $[64] = isSearchFocused;
    $[65] = mobileView;
    $[66] = selectedHeatmapEventId;
    $[67] = selectedMarkerId;
    $[68] = t21;
    $[69] = t22;
  } else {
    t21 = $[68];
    t22 = $[69];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[70] !== isOpen || $[71] !== isSearchFocused) {
    t23 = () => {
      if (isSearchFocused && !isOpen) {
        setIsOpen(true);
      }
    };
    t24 = [isSearchFocused, isOpen];
    $[70] = isOpen;
    $[71] = isSearchFocused;
    $[72] = t23;
    $[73] = t24;
  } else {
    t23 = $[72];
    t24 = $[73];
  }
  useEffect(t23, t24);
  let t25;
  if ($[74] !== exitAskAnything || $[75] !== exitChecklist || $[76] !== exitExplore || $[77] !== exitHeroAction || $[78] !== exitSessionHistory || $[79] !== mobileView) {
    t25 = () => {
      setIsSearchFocused(false);
      if (mobileView === "sessionHistory") {
        exitSessionHistory();
      } else {
        if (mobileView === "checklist") {
          exitChecklist();
        } else {
          if (mobileView === "explore") {
            exitExplore();
          } else {
            if (mobileView === "askAnything") {
              exitAskAnything();
            } else {
              if (mobileView === "heroAction") {
                exitHeroAction();
              }
            }
          }
        }
      }
    };
    $[74] = exitAskAnything;
    $[75] = exitChecklist;
    $[76] = exitExplore;
    $[77] = exitHeroAction;
    $[78] = exitSessionHistory;
    $[79] = mobileView;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  const handleOverlayClick = t25;
  if (hostType === HostType.WEB && hostMode === HostMode.EMBED) {
    return null;
  }
  let t26;
  if ($[81] !== clearSelectedMarkerInMap || $[82] !== dispatch || $[83] !== teleportCta) {
    t26 = open => {
      if (!open) {
        teleportCta.close();
        clearSelectedMarkerInMap();
        dispatch(clearSelectedHeatmapEvent());
      }
      setIsOpen(open);
    };
    $[81] = clearSelectedMarkerInMap;
    $[82] = dispatch;
    $[83] = teleportCta;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  const handleOpenChange = t26;
  let t27;
  if ($[85] !== selectMarkerInMap) {
    t27 = id => {
      selectMarkerInMap(id, {
        selectSource: "drawer_nearby"
      });
    };
    $[85] = selectMarkerInMap;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  const handlePinSelect = t27;
  let t28;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = css({
      borderTopLeftRadius: "32",
      borderTopRightRadius: "32",
      overflow: "hidden",
      height: "full"
    });
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  const mobileViewContentStyles = t28;
  let t29;
  if ($[88] !== enterChecklist || $[89] !== enterSessionHistory || $[90] !== exitAskAnything || $[91] !== exitChecklist || $[92] !== exitCustomChecklist || $[93] !== exitExplore || $[94] !== exitHeroAction || $[95] !== exitSessionHistory || $[96] !== exitSharedChecklist || $[97] !== handlePinSelect || $[98] !== mobileView || $[99] !== mobileViewContentStyles || $[100] !== nearbyPins || $[101] !== selectedHeatmapEvent || $[102] !== selectedLocation?.properties || $[103] !== selectedMarkerId || $[104] !== teleportCta.close || $[105] !== teleportCta.handleDownloadClick || $[106] !== teleportCta.isOpen) {
    t29 = () => {
      if (teleportCta.isOpen) {
        return <TeleportCtaCard isMobilePane={true} onClose={teleportCta.close} onDownloadClick={teleportCta.handleDownloadClick} />;
      }
      if (mobileView === "heroAction") {
        return <div className={mobileViewContentStyles}><HeroActionSidebarView onBack={exitHeroAction} isMobilePane={true} onSearchFocus={() => setIsSearchFocused(true)} /></div>;
      }
      if (mobileView === "askAnything") {
        return <div className={mobileViewContentStyles}><AskAnythingSidebarView onBack={exitAskAnything} isMobilePane={true} onSearchFocus={() => setIsSearchFocused(true)} /></div>;
      }
      if (selectedMarkerId) {
        return <MapLocationContent isMobilePane={true} nearbyPins={nearbyPins} onPinSelect={handlePinSelect} selectedPinId={selectedLocation?.properties.externalId} />;
      }
      if (selectedHeatmapEvent) {
        return <div className={mobileViewContentStyles}><HeatmapEventInfo isMobilePane={true} /></div>;
      }
      if (mobileView === "sessionHistory") {
        return <div className={mobileViewContentStyles}><SessionHistoryView onBack={exitSessionHistory} isMobilePane={true} /></div>;
      }
      if (mobileView === "checklist") {
        return <div className={mobileViewContentStyles}><ChecklistSidebarView onBack={exitChecklist} isMobilePane={true} onSearchFocus={() => setIsSearchFocused(true)} /></div>;
      }
      if (mobileView === "explore") {
        return <div className={mobileViewContentStyles}><ExploreSidebarView onBack={exitExplore} isMobilePane={true} onSearchFocus={() => setIsSearchFocused(true)} /></div>;
      }
      if (mobileView === "customChecklist") {
        return <div className={mobileViewContentStyles}><CustomChecklistSidebarView onBack={exitCustomChecklist} isMobilePane={true} onSearchFocus={() => setIsSearchFocused(true)} /></div>;
      }
      if (mobileView === "sharedChecklist") {
        return <div className={mobileViewContentStyles}><SharedChecklistSidebarView onBack={exitSharedChecklist} isMobilePane={true} onSearchFocus={() => setIsSearchFocused(true)} /></div>;
      }
      return <MobileSidebarContent onSearchFocusChange={setIsSearchFocused} onSessionHistoryClick={enterSessionHistory} onChecklistClick={enterChecklist} />;
    };
    $[88] = enterChecklist;
    $[89] = enterSessionHistory;
    $[90] = exitAskAnything;
    $[91] = exitChecklist;
    $[92] = exitCustomChecklist;
    $[93] = exitExplore;
    $[94] = exitHeroAction;
    $[95] = exitSessionHistory;
    $[96] = exitSharedChecklist;
    $[97] = handlePinSelect;
    $[98] = mobileView;
    $[99] = mobileViewContentStyles;
    $[100] = nearbyPins;
    $[101] = selectedHeatmapEvent;
    $[102] = selectedLocation?.properties;
    $[103] = selectedMarkerId;
    $[104] = teleportCta.close;
    $[105] = teleportCta.handleDownloadClick;
    $[106] = teleportCta.isOpen;
    $[107] = t29;
  } else {
    t29 = $[107];
  }
  const renderContent = t29;
  const t30 = currentViewKey !== "location" && currentViewKey !== "default" || isSelectedPinExploreActionMenu;
  const t31 = `${selectedMarkerId || selectedHeatmapEventId || mobileView}-${mobileView}-${isEditingCustomPin ? "edit" : "view"}-${teleportCta.isOpen ? "teleport" : "details"}`;
  const t32 = selectedMarkerId || selectedHeatmapEventId ? PIN_VIEW_PEEK_HEIGHT : DEFAULT_VIEW_PEEK_HEIGHT;
  const t33 = !selectedMarkerId && (isSearchFocused || mobileView === "checklist" || mobileView === "sessionHistory" || mobileView === "explore" || mobileView === "askAnything" || mobileView === "heroAction");
  let t34;
  let t35;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = css({
      width: "full",
      maxWidth: "full",
      paddingX: "0",
      background: "[linear-gradient(rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.075)), linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9))]",
      backdropFilter: "[blur(16px)]"
    });
    t35 = css({
      width: "full",
      height: "full",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    });
    $[108] = t34;
    $[109] = t35;
  } else {
    t34 = $[108];
    t35 = $[109];
  }
  const t36 = currentViewKey === "sessionHistory" || currentViewKey === "checklist" || currentViewKey === "explore" || currentViewKey === "askAnything" || currentViewKey === "heroAction" ? handleContentTouchStart : undefined;
  const t37 = currentViewKey === "sessionHistory" || currentViewKey === "checklist" || currentViewKey === "explore" || currentViewKey === "askAnything" || currentViewKey === "heroAction" ? handleSwipeDismiss : undefined;
  let t38;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = css({
      width: "full",
      flex: "1",
      minHeight: "[0]"
    });
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  const t39 = `${t38} ${viewEnterStyles}`;
  let t40;
  if ($[111] !== renderContent) {
    t40 = renderContent();
    $[111] = renderContent;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== currentViewKey || $[114] !== t39 || $[115] !== t40) {
    t41 = <div key={currentViewKey} className={t39}>{t40}</div>;
    $[113] = currentViewKey;
    $[114] = t39;
    $[115] = t40;
    $[116] = t41;
  } else {
    t41 = $[116];
  }
  let t42;
  if ($[117] !== t35 || $[118] !== t36 || $[119] !== t37 || $[120] !== t41) {
    t42 = <div className={t35} onTouchStart={t36} onTouchEnd={t37}>{t41}</div>;
    $[117] = t35;
    $[118] = t36;
    $[119] = t37;
    $[120] = t41;
    $[121] = t42;
  } else {
    t42 = $[121];
  }
  let t43;
  if ($[122] !== drawerDragTopReserve || $[123] !== handleOpenChange || $[124] !== handleOverlayClick || $[125] !== isOpen || $[126] !== locationSnapPoints || $[127] !== t30 || $[128] !== t31 || $[129] !== t32 || $[130] !== t33 || $[131] !== t34 || $[132] !== t42) {
    t43 = <MobileDrawer isOpen={isOpen} onOpenChange={handleOpenChange} fitContent={t30} contentKey={t31} minHeight={t32} topReserve={DRAWER_TOP_RESERVE} dragTopReserve={drawerDragTopReserve} snapPoints={locationSnapPoints} forceExpanded={t33} initialSnapIndex={1} showOverlay={true} overlayOpacity={0.5} onOverlayClick={handleOverlayClick} className={t34}>{t42}</MobileDrawer>;
    $[122] = drawerDragTopReserve;
    $[123] = handleOpenChange;
    $[124] = handleOverlayClick;
    $[125] = isOpen;
    $[126] = locationSnapPoints;
    $[127] = t30;
    $[128] = t31;
    $[129] = t32;
    $[130] = t33;
    $[131] = t34;
    $[132] = t42;
    $[133] = t43;
  } else {
    t43 = $[133];
  }
  return t43;
};